import React from 'react';
import PropTypes from 'prop-types';
import i18n from '../../../i18n';
import {
    Tooltip,
    IconButton,
    Typography,
    InputAdornment,
    TextField,
    Box,
    Badge,
    FormControl,
    FormHelperText
} from '@mui/material';
import Iconify from '../../../components/iconify';
import FilterItemsAbstract from '../../../pages/FilterAbstarct/FilterItemsAbstract';
import InputMaskCustom from '../../../components/input-mask/InputMaskCustom';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ListIcon from '@mui/icons-material/List';
import ArchiveIcon from '@mui/icons-material/Archive';
// hooks
import useMediaQuery from '@mui/material/useMediaQuery';
import {
    CompareArrows,
    FilterList,
    LockOutlined,
    Search
} from '@mui/icons-material';
import { validateRange } from '../../../utils/dateRangeHelper';
import { StyledSearch } from '../../../components/StyledSearch';
import { StyledRoot } from '../../../components/StyledRoot';

const SelectionInfo = ({ numSelected }) => (
    <Typography component="div" variant="subtitle1">
        {numSelected} selected
    </Typography>
);

const SearchInput = ({
    value,
    onChange,
    onKeyDown,
    handleCloseSearch,
    disabled,
    helperText,
    maxWidth
}) => (
    <FormControl>
        <StyledSearch
            value={value}
            disabled={disabled}
            sx={{
                cursor: disabled ? 'not-allowed' : 'text',
                backgroundColor: disabled ? '#fafafa' : 'inherit',
                maxWidth: maxWidth
            }}
            inputProps={{
                title: disabled ? 'Disabled while filter is active' : undefined
            }}
            onChange={(event) => onChange(event.target.value)}
            onKeyDown={onKeyDown}
            placeholder={i18n.t('text.search')}
            startAdornment={
                <InputAdornment position="start">
                    <Iconify
                        icon="eva:search-fill"
                        sx={{ color: 'text.disabled', width: 20, height: 20 }}
                    />
                </InputAdornment>
            }
            endAdornment={
                <IconButton edge="end" onClick={handleCloseSearch}>
                    <Iconify icon="eva:close-fill" />
                </IconButton>
            }
        />
        <FormHelperText sx={{ marginLeft: 0, color: '#919EAB' }}>
            {helperText}
        </FormHelperText>
    </FormControl>
);

const GridListToolbar = ({
    numSelected,
    searchValue,
    handleSearchData,
    handleSearchKeyDown,
    onDelete,
    Head,
    handleFilterItems,
    handleTableSettings,
    NeedSearch,
    onRangeDate,
    onClearRangeDate,
    onSaveRangeDate,
    rangeValue,
    handleOpenDateRange,
    showDatePicker,
    exportButton,
    exportDataToCsv,
    params,
    navigate,
    controller,
    deletePermission,
    filterRequest,
    filterActive,
    disableDateRange,
    disableDateRangeText,
    limitDateRange,
    archivePermission,
    handleArchive,
    toggleArchived,
    lockInstance,
    lockInstancePerm,
    transferBalancePerm,
    transferBalance
}) => {
    const handleCloseSearch = () => {
        console.log('close search');
        if (!searchValue) return;
        handleSearchData('');
    };
    const [archiveActive, setArchiveActive] = React.useState(false);
    const handleToggleArchived = () => {
        setArchiveActive(!archiveActive);
        toggleArchived(!archiveActive);
    };

    const mobileView = useMediaQuery((theme) => theme.breakpoints.down('md'));

    const renderRangeDateField = () => {
        if (!showDatePicker) {
            return <></>;
        }

        const isValidRange = validateRange(rangeValue, limitDateRange);
        // const DATE_RANGE_REGEX =
        //     /^\d{2}\/\d{2}\/\d{4}-\d{2}\/\d{2}\/\d{4}$/;
        return (
            <TextField
                id="date"
                label="Date range"
                type="text"
                name="date"
                placeholder="DD/MM/YYYY - DD/MM/YYYY"
                value={rangeValue}
                disabled={disableDateRange}
                helperText={
                    disableDateRangeText ||
                    (rangeValue && !isValidRange
                        ? `Maximum ${limitDateRange} days range`
                        : null)
                }
                error={!!rangeValue && !isValidRange}
                variant="outlined"
                onChange={(e) => {
                    // if (DATE_RANGE_REGEX.test(rangeValue)) {
                    //     onSaveRangeDate(rangeValue);
                    // }
                    onRangeDate(e.target.value);
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        if (!isValidRange) {
                            e.preventDefault();
                            return;
                        }
                        onSaveRangeDate(rangeValue);
                    } else if (e.key === 'Escape') {
                        onClearRangeDate();
                    }
                }}
                sx={{ width: '100%', maxWidth: 300, minWidth: 150 }}
                InputProps={{
                    inputComponent: InputMaskCustom,
                    endAdornment: !disableDateRange && (
                        <InputAdornment position="end">
                            {rangeValue && rangeValue.length > 0 ? (
                                <>
                                    <IconButton
                                        onClick={() => onClearRangeDate()}
                                    >
                                        <Iconify icon="eva:close-fill" />
                                    </IconButton>
                                </>
                            ) : (
                                <></>
                            )}
                            <IconButton onClick={handleOpenDateRange}>
                                <Iconify icon="eva:calendar-fill" />
                            </IconButton>
                        </InputAdornment>
                    )
                }}
            />
        );
    };

    const renderExportButton = () => {
        if (!exportButton) {
            return <></>;
        }
        return (
            <Tooltip title={i18n.t('buttons.exportCsv')}>
                <IconButton onClick={exportDataToCsv}>
                    <FileDownloadIcon />
                </IconButton>
            </Tooltip>
        );
    };

    const renderSearch = () => {
        return mobileView ? (
            openSearch ? (
                <SearchInput
                    value={searchValue}
                    // helperText={' '}
                    // disabled={!!filterActive}
                    onChange={handleSearchData}
                    onKeyDown={handleSearchKeyDown}
                    handleCloseSearch={handleCloseSearch}
                    sx={{
                        width: '100%',
                        maxWidth: '250px',
                        m: 1,
                        flex: '1 1 0',
                        minWidth: '100px'
                    }}
                />
            ) : (
                <IconButton
                    // disabled={!!filterActive}
                    onClick={() => {
                        setOpenSearch(true);
                        setOpenFilter(false);
                    }}
                >
                    <Search />
                </IconButton>
            )
        ) : (
            <SearchInput
                value={searchValue}
                // helperText={filterActive ? i18n.t('text.searchHint') : null}
                // disabled={!!filterActive}
                onChange={handleSearchData}
                onKeyDown={handleSearchKeyDown}
                handleCloseSearch={handleCloseSearch}
                sx={{
                    width: '100%',
                    maxWidth: '250px',
                    m: 1,
                    flex: '1 1 0',
                    minWidth: '100px'
                }}
            />
        );
    };

    const renderFilter = () => {
        return mobileView ? (
            openFilter ? (
                <FilterItemsAbstract
                    Head={Head}
                    handleFilterItems={handleFilterItems}
                    params={params}
                    navigate={navigate}
                    controller={controller}
                    filters={filterRequest}
                    // disabled={!!searchValue}
                    // helperText={' '}
                    maxWidth={250}
                />
            ) : (
                <IconButton
                    onClick={() => {
                        setOpenFilter(true);
                        setOpenSearch(false);
                    }}
                    // disabled={!!searchValue}
                >
                    <FilterList />
                </IconButton>
            )
        ) : (
            <FilterItemsAbstract
                Head={Head}
                handleFilterItems={handleFilterItems}
                params={params}
                navigate={navigate}
                controller={controller}
                filters={filterRequest}
                // disabled={!!searchValue}
                // helperText={searchValue ? i18n.t('text.filterHint') : null}
            />
        );
    };

    const [openSearch, setOpenSearch] = React.useState(false);
    const [openFilter, setOpenFilter] = React.useState(false);

    return (
        <StyledRoot
            sx={{
                ...(numSelected > 0 && {
                    color: 'primary.main',
                    bgcolor: 'primary.lighter'
                })
            }}
        >
            {numSelected > 0 ? (
                <SelectionInfo numSelected={numSelected} />
            ) : (
                <>
                    <Box
                        display="flex"
                        justifyContent="start"
                        alignItems="center"
                        width="100%"
                        gap={1}
                    >
                        {NeedSearch && renderSearch()}
                        {renderFilter()}
                    </Box>
                    <Box
                        display="flex"
                        justifyContent="end"
                        alignItems="center"
                        width="100%"
                        gap={{ sm: 0, md: 1, lg: 2, xl: 2 }}
                    >
                        {!mobileView &&
                            showDatePicker &&
                            renderRangeDateField()}

                        {archivePermission && (
                            <Tooltip
                                title={
                                    archiveActive
                                        ? 'Show active'
                                        : 'Show archived'
                                }
                            >
                                <IconButton
                                    onClick={() => handleToggleArchived()}
                                >
                                    {archiveActive ? (
                                        <ListIcon />
                                    ) : (
                                        <ArchiveIcon />
                                    )}
                                </IconButton>
                            </Tooltip>
                        )}
                        {mobileView && showDatePicker && (
                            <IconButton
                                aria-label="calendar"
                                onClick={handleOpenDateRange}
                                disabled={disableDateRange}
                            >
                                <Badge
                                    color="primary"
                                    variant="dot"
                                    invisible={
                                        !rangeValue || rangeValue.length === 0
                                    }
                                >
                                    <Iconify icon={'eva:calendar-outline'} />
                                </Badge>
                            </IconButton>
                        )}
                        <Tooltip title="Table Settings">
                            <IconButton
                                aria-label="settings"
                                onClick={handleTableSettings}
                            >
                                <Iconify icon="eva:settings-2-fill" />
                            </IconButton>
                        </Tooltip>
                        {renderExportButton()}
                        {lockInstancePerm && (
                            <Tooltip title="Lock Instance">
                                <IconButton
                                    aria-label="lock"
                                    onClick={lockInstance}
                                >
                                    <LockOutlined />
                                </IconButton>
                            </Tooltip>
                        )}
                        {transferBalancePerm && (
                            <Tooltip title="Transfer Balance">
                                <IconButton
                                    aria-label="transfer"
                                    onClick={transferBalance}
                                >
                                    <CompareArrows />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                </>
            )}

            {numSelected > 0 && (
                <div>
                    {archivePermission && (
                        <Tooltip title="Archive">
                            <IconButton onClick={handleArchive}>
                                <Iconify icon="eva:archive-fill" />
                            </IconButton>
                        </Tooltip>
                    )}
                    {deletePermission && (
                        <Tooltip title="Delete">
                            <IconButton onClick={onDelete}>
                                <Iconify icon="eva:trash-2-fill" />
                            </IconButton>
                        </Tooltip>
                    )}
                </div>
            )}
        </StyledRoot>
    );
};

GridListToolbar.propTypes = {
    numSelected: PropTypes.number,
    searchValue: PropTypes.string,
    handleSearchData: PropTypes.func,
    onDelete: PropTypes.func,
    Head: PropTypes.array,
    handleFilterItems: PropTypes.func,
    handleTableSettings: PropTypes.func,
    NeedSearch: PropTypes.bool,
    clientId: PropTypes.string,
    handleOpenDateRange: PropTypes.func,
    onRangeDate: PropTypes.func,
    onClearRangeDate: PropTypes.func,
    onSaveRangeDate: PropTypes.func,
    rangeValue: PropTypes.string,
    showDatePicker: PropTypes.bool,
    exportButton: PropTypes.bool,
    exportDataToCsv: PropTypes.func,
    deletePermission: PropTypes.bool
};

export default GridListToolbar;
